import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { AnalyticsService } from '../../../../services/analytics/analytics.service';
import { sendInterestToSheet } from '../../../../services/analytics/event-tracker.service';
import { submitFeatureRequest, submitFeatureRequestFailure, submitFeatureRequestSuccess } from '../../../../state';
import { CtaButtonComponent } from '../../../../ui/cta-button/cta-button.component';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'wil-professional-plan-feature-promotion',
  standalone: true,
  imports: [ReactiveFormsModule, CtaButtonComponent, MatIcon],
  templateUrl: './professional-plan-feature-promotion.component.html',
  styleUrl: './professional-plan-feature-promotion.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfessionalPlanFeaturePromotionComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly analytics = inject(AnalyticsService);
  private readonly cdr = inject(ChangeDetectorRef);

  public readonly isSubmitted = signal(false);
  public readonly isLoading = signal(false);
  public readonly hasError = signal(false);

  public readonly waitlistForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    plan: ['Professional'],
    nickname: [''],
  });

  public get isFormValid(): boolean {
    return this.waitlistForm.valid && !this.isLoading();
  }

  public ngOnInit(): void {
    this.analytics.track('professional_interest_viewed', { source: 'home_page' });

    this.actions$.pipe(ofType(submitFeatureRequestSuccess), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isSubmitted.set(true);
      this.waitlistForm.disable();
      this.isLoading.set(false);
      this.analytics.track('professional_interest_submitted', { result: 'success', source: 'home_page' });
      this.cdr.markForCheck();
    });

    this.actions$.pipe(ofType(submitFeatureRequestFailure), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.hasError.set(true);
      this.isLoading.set(false);
      this.analytics.track('professional_interest_submitted', { result: 'error', source: 'home_page' });
      this.cdr.markForCheck();
    });
  }

  public onSubmit(): void {
    if (!this.isFormValid) {
      this.waitlistForm.markAllAsTouched();
      return;
    }

    const formData = this.waitlistForm.getRawValue();
    if (formData.nickname) {
      return;
    }

    this.isLoading.set(true);
    this.hasError.set(false);

    const request = {
      email: formData.email!,
      plan: formData.plan!,
      nickname: formData.nickname!,
      event: 'professional_interest',
      sentAt: new Date().toISOString(),
    };

    try {
      sendInterestToSheet(request.email, 'professional_cta', request.plan, request.event);
    } catch {
      // The NgRx request remains the source of truth if optional analytics submission fails.
    }

    this.store.dispatch(submitFeatureRequest({ request }));
  }
}
